// /visa/documents — Wave 66: TH hub of the six visa document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/visa/documents";
const URL = `${SITE}${PATH}`;
const TITLE = "เอกสารประกอบวีซ่าที่ต้องแปลและรับรอง — ครบ 6 กลุ่ม | NYC";
const DESC =
  "รวม 6 กลุ่มเอกสารประกอบคำร้องขอวีซ่า พร้อมลำดับขั้นตอนแปล–รับรองนิติกรณ์–สถานทูต เช็กลิสต์ และคำถามที่พบบ่อย อ้างอิงกรมการกงสุลและหน่วยงานผู้ออกเอกสาร";

export const Route = createFileRoute("/visa/documents/")({
  loader: async () => {
    const { VISA_DOC_GROUPS } = await import("@/data/visa-doc-groups");
    return {
      __d: { VISA_DOC_GROUPS },
      groups: VISA_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/visa/documents` },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กลุ่มเอกสารประกอบวีซ่า",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: VisaDocumentsPage,
});

function VisaDocumentsPage() {
  const { __d: { VISA_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/visa-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/visa" className="hover:underline">
          บริการวีซ่า
        </Link>{" "}
        / เอกสารประกอบวีซ่า
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        เอกสารประกอบวีซ่าที่ต้องแปลและรับรอง — ครบ 6 กลุ่ม
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        เอกสารประกอบคำร้องขอวีซ่าที่ออกเป็นภาษาไทยต้องมีคำแปลพร้อมคำรับรองความถูกต้อง และในหลายประเทศต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุล
        กระทรวงการต่างประเทศ ก่อนยื่นสถานทูตปลายทาง หน้านี้แยกเอกสารตามลักษณะการใช้งาน พร้อมลำดับขั้นตอนและเช็กลิสต์ของแต่ละกลุ่ม
        สอบถามรายละเอียดเฉพาะกรณีได้ทางโทรศัพท์ LINE หรืออีเมล
      </p>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {VISA_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link to="/visa/documents/$group" params={{ group: g.slug }} className="text-primary hover:underline">
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>

      {VISA_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/visa/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            อ่านคู่มือ {g.titleTh} ฉบับเต็ม →
          </Link>
        </section>
      ))}
          <ServiceAuthorityBlock topic="visa" locale="th" />
    </main>
  );
}
